/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import * as React from 'react';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { addTracker } from 'src/core/utils/decorators/addTracker';
import {
    appsToEmailSwaggerToAppToEmailStores,
    appsToExportSwaggerToAppToExportStores
} from 'src/verticals/azureMigrationModal/handlers/azureMigrationMappers';
import { getAzureMigrationService } from 'src/verticals/azureMigrationModal/services/azureMigrationService';
import { AppsToEmailSwagger, AppsToExportSwagger } from 'src/verticals/azureMigrationModal/services/azureMigrationSwagger';
import { AppToEmailStore } from 'src/verticals/azureMigrationModal/stores/AppToEmailStore';
import { AppToExportStore } from 'src/verticals/azureMigrationModal/stores/AppToExportStore';

export type useAzureMigrationType = ReturnType<typeof useAzureMigration>;

/**
 * Contains functions to get and store the applications
 * that are either owned by a non migrated user and have
 * collaborators on them, or apps that the non migrated
 * user is a collaborator on but owned by someone else.
 */
export const useAzureMigration = () => {
    const { rootStore } = useGlobalState();
    const [appsToEmail, setAppsToEmail] = React.useState<AppToEmailStore[]>([]);
    const [appsToExport, setAppsToExport] = React.useState<AppToExportStore[]>([]);

    const appsToEmailTracker = useProgressTracker();
    const appsToExportTracker = useProgressTracker();
    const logger = React.useRef(getLogger());

    const azureMigrationService = React.useMemo(getAzureMigrationService, []);

    useAsyncEffect(async () => {
        await Promise.all([getAppsToEmail(), getAppsToExport()]);
    }, []);

    /**
     * Gets the apps that the user owns but has collaborators on. These
     * collaborators should then be sent emails to notify them that the
     * user will migrate and they will no longer have access to these
     * apps.
     */
    const getAppsToEmail = async () =>
        flow(async () => {
            const callLogger = logger.current.createTimedActivityLogger<AppsToEmailSwagger[]>('LoadAppsToEmail');

            const tenantToken = rootStore.azureStore.session.userTokens.armToken;
            const apps: AppToEmailStore[] = [];

            await Promise.all(
                rootStore.userMigrationInfoStoreHouse.items.map(async store => {
                    const appsToEmailSwaggers = await callLogger(
                        azureMigrationService.getAppsToEmail({ tenantToken, endpoint: store.endpoint, serviceKey: store.serviceKey })
                    );
                    const appsToEmailStore = appsToEmailSwaggers?.map(s => appsToEmailSwaggerToAppToEmailStores(s, store.region));

                    if (appsToEmailStore) {
                        apps.push(...appsToEmailStore);
                    }
                })
            );

            setAppsToEmail(apps);
        }, addTracker({ tracker: appsToEmailTracker }))();

    /**
     * Gets the apps that the user is a collaborator on but does not own.
     * When the user migrates, they will lose access to these apps, thus
     * it is important for them to export these apps.
     */
    const getAppsToExport = async () =>
        flow(async () => {
            const callLogger = logger.current.createTimedActivityLogger<AppsToExportSwagger[]>('LoadAppsToExport');
            const apps: AppToExportStore[] = [];

            await Promise.all(
                rootStore.userMigrationInfoStoreHouse.items.map(async store => {
                    const appsToExportSwaggers = await callLogger(
                        azureMigrationService.getAppsToExport({ serviceKey: store.serviceKey, endpoint: store.endpoint })
                    );
                    const appsToExportStore = appsToExportSwaggers?.map(s => appsToExportSwaggerToAppToExportStores(s, store.region));

                    if (appsToExportStore) {
                        apps.push(...appsToExportStore);
                    }
                })
            );

            setAppsToExport(apps);
        }, addTracker({ tracker: appsToExportTracker }))();

    return { appsToEmail, appsToEmailTracker, appsToExport, appsToExportTracker };
};
